import { t } from '../common/i18n.js'
import { Language } from '../common/types/i18n.js'
import { ThemePreference } from '../common/types/theme.js'
import { isExternalUrl } from '../common/utils/url-utils.js'
import { PortalHeroCallout, PortalHeroLink } from './components/portal-hero/portal-hero.js'
import { navGroups, visibleGroups } from './nav-config.js'
import { emptyPortalPermissions } from './portal-role.js'
import { PortalLink, View } from './types.js'
import {
    loadPlaytimeProfileIfNeeded,
    loadPortalLinksIfNeeded,
    loadPortalRoleIfNeeded,
} from './utils/load-utils.js'
import { setTheme, ThemeSelectHandler } from './utils/theme.js'

/** Teemavaliku sildid kasutaja keeles — jagatud hero, draweri ja seadete vahel. */
export const themeLabels = (lang: Language): Record<ThemePreference, string> => ({
    system: t.theme.system(lang),
    light: t.theme.light(lang),
    dark: t.theme.dark(lang),
})

export interface HeroData {
    userName: string
    userFullName: string
    subtitle: string
    quickLinks: PortalHeroLink[]
    accountLinks: PortalHeroLink[]
    callout?: PortalHeroCallout
    userAvatar?: string
    activeLang: 'et' | 'en'
    themePreference: ThemePreference
    onSelectTheme: ThemeSelectHandler
    themeLabels: Record<ThemePreference, string>
    onLogout: () => void
}

// Fallback links used when the API hasn't responded yet: the audience-'all'
// subset of the seeded hero links — the org-specific ones (Jira, Drive,
// Harvest, Forecast, Bitbucket) need the server to know who is asking.
const fallbackQuickLinks = [
    { label: 'Siseveeb', url: 'https://siseveeb.playnope.com' },
    { label: 'Notion', url: 'https://notion.playnope.com' },
    { label: 'Asset Manager', url: 'https://asset.playnope.com' },
    { label: 'Playcare', url: 'https://support.play.ee' },
    { label: '1Password', url: 'https://1password.playnope.com' },
    { label: 'Slack', url: 'https://slack.playnope.com' },
].map(withExternalFlags)

function withExternalFlags<T extends { url: string }>(link: T) {
    const external = isExternalUrl(link.url)
    return { ...link, isExternal: external, openInNewTab: external }
}

const toLinkProps = (links: PortalLink[]): PortalHeroLink[] =>
    links.map((link) => withExternalFlags({ label: link.label, url: link.url }))

const hasCategory = (link: PortalLink, category: string) =>
    link.category.split(',').includes(category)

// Greeting shown in the hero: the user's first name, or a friendly fallback.
export const portalGreeting = (name: string | undefined, lang: Language): string => {
    const firstName = name?.split(' ')[0]
    if (firstName) return firstName
    return t.portalHero.greetingFallback(lang)
}

// Builds the subset of hero props that the global top bar and the legacy portal
// model share: greeting, subtitle, quick/account links, the Q12 callout, the
// user's avatar, admin flag, language, and logout. Tile/section logic lives in
// models/portal.tsx and is intentionally not duplicated here.
export const buildHeroData = (view: View): HeroData => {
    const { lang } = view.state

    const linksState = loadPortalLinksIfNeeded(view)
    const profileState = loadPlaytimeProfileIfNeeded(view)

    const portalLinks = linksState.data ?? []
    const profile = profileState.data

    const heroLinks = portalLinks.filter((l) => hasCategory(l, 'hero'))
    const quickLinkProps = heroLinks.length > 0 ? toLinkProps(heroLinks) : fallbackQuickLinks

    // The avatar dropdown shows the personal "Me" profile links — the same
    // role-aware set rendered in the sidebar's "Mina" group (nav-config.ts), so
    // the two stay in sync. We drop the dashboard (reachable via the brand/home
    // link) and any external items, leaving the six profile pages.
    const permissions = loadPortalRoleIfNeeded(view).data?.permissions ?? emptyPortalPermissions()
    const meGroup = visibleGroups(navGroups, permissions).find(
        (g) => g.label === t.portal.nav.meSection,
    )
    const accountLinkProps: PortalHeroLink[] = (meGroup?.items ?? [])
        .filter((item) => !item.external && item.path.split('?')[0] !== '/')
        .map((item) => ({ label: item.label(lang), url: item.path, isExternal: false }))

    const surveyOpen = view.state.hasActiveCampaign && !view.state.activeCampaignAnswered
    const callout: PortalHeroCallout = {
        text: surveyOpen
            ? t.portalHero.callout.activeSurveyText(lang)
            : t.portalHero.callout.inactiveSurveyText(lang),
        ctaLabel: surveyOpen
            ? t.portalHero.callout.startSurvey(lang)
            : t.portalHero.callout.viewResults(lang),
        ctaUrl:
            surveyOpen && view.state.activeCampaignId
                ? `/survey/${view.state.activeCampaignId}`
                : '/badges',
        deadline:
            surveyOpen && view.state.activeCampaignEnd
                ? t.portalHero.q12Tile.deadline(view.state.activeCampaignEnd.slice(0, 10))(lang)
                : undefined,
    }

    return {
        userName: portalGreeting(view.state.user?.name, lang),
        userFullName: view.state.user?.name ?? '',
        subtitle: t.portalHero.subtitle(lang),
        quickLinks: quickLinkProps,
        accountLinks: accountLinkProps,
        callout,
        userAvatar: view.state.user ? (profile?.avatarUrl ?? undefined) : undefined,
        activeLang: lang,
        themePreference: view.state.themePreference,
        onSelectTheme: (pref, origin) => setTheme(view, pref, origin),
        themeLabels: themeLabels(lang),
        onLogout: () => {
            void fetch('/api/logout', { method: 'POST' }).then(() => {
                window.location.href = '/'
            })
        },
    }
}
